import { Component, inject, Input, OnInit } from '@angular/core';
import { IonicModule, ModalController } from '@ionic/angular';

import { AuthStateService, Show } from '../../../auth/auth-state.service';

/**
 * Filter display mode for showreport list.
 */
export type ShowFilterDisplayMode = 'active-show' | 'all-shows' | 'selected-shows';

/**
 * Show filter configuration.
 */
export interface ShowFilter {
    displayMode: ShowFilterDisplayMode;
    selectedShowIds: number[];
}

/**
 * Modal component for filtering showreports by show.
 * Users can choose to view:
 * - Active show only (default)
 * - All linked shows
 * - Specific selected shows
 */
@Component({
    selector: 'app-show-filter-modal',
    templateUrl: './show-filter-modal.component.html',
    styleUrls: ['./show-filter-modal.component.scss'],
    imports: [
    IonicModule
]
})
export class ShowFilterModalComponent implements OnInit {

    @Input() currentFilter: ShowFilter = {
        displayMode: 'active-show',
        selectedShowIds: []
    };

    public displayMode: ShowFilterDisplayMode = 'active-show';
    public selectedShowIds: number[] = [];
    public availableShows: Show[] = [];
    public activeShow: Show | null = null;

    private modalController: ModalController = inject(ModalController);
    private authStateService: AuthStateService = inject(AuthStateService);

    ngOnInit(): void {
        // Load available shows from auth state
        this.availableShows = this.authStateService.availableShows() ?? [];
        this.activeShow = this.authStateService.activeShow() ?? null;

        // Initialize from current filter
        this.displayMode = this.currentFilter.displayMode;
        this.selectedShowIds = [...this.currentFilter.selectedShowIds];

        // If selected-shows mode but no shows selected, pre-select active show
        if (this.displayMode === 'selected-shows' && this.selectedShowIds.length === 0 && this.activeShow) {
            this.selectedShowIds = [this.activeShow.id];
        }
    }

    /**
     * Pick a display mode by tapping the entire row (the radio control
     * is no longer rendered — the row itself is the button).
     */
    public setDisplayMode(mode: ShowFilterDisplayMode): void {
        this.displayMode = mode;
        this.onDisplayModeChange();
    }

    public onDisplayModeChange(): void {
        // When switching to selected-shows, pre-select active show if nothing selected
        if (this.displayMode === 'selected-shows' && this.selectedShowIds.length === 0 && this.activeShow) {
            this.selectedShowIds = [this.activeShow.id];
        }
    }

    public isShowSelected(showId: number): boolean {
        return this.selectedShowIds.includes(showId);
    }

    public toggleShowSelection(showId: number): void {
        const index = this.selectedShowIds.indexOf(showId);
        if (index > -1) {
            this.selectedShowIds.splice(index, 1);
        } else {
            this.selectedShowIds.push(showId);
        }
    }

    public getFilterLabel(): string {
        switch (this.displayMode) {
            case 'active-show':
                return this.activeShow?.name ?? 'Actieve productie';
            case 'all-shows':
                return 'Alle producties';
            case 'selected-shows':
                if (this.selectedShowIds.length === 0) {
                    return 'Geen selectie';
                }
                if (this.selectedShowIds.length === 1) {
                    const show = this.availableShows.find(s => s.id === this.selectedShowIds[0]);
                    return show?.name ?? '1 productie';
                }
                return `${this.selectedShowIds.length} producties`;
            default:
                return 'Filter';
        }
    }

    public canConfirm(): boolean {
        // Can always confirm except when in selected-shows mode with no shows selected
        if (this.displayMode === 'selected-shows') {
            return this.selectedShowIds.length > 0;
        }
        return true;
    }

    public async confirmSelection(): Promise<void> {
        const filter: ShowFilter = {
            displayMode: this.displayMode,
            selectedShowIds: this.displayMode === 'selected-shows' ? [...this.selectedShowIds] : []
        };

        await this.modalController.dismiss(filter, 'confirm');
    }

    public async cancel(): Promise<void> {
        await this.modalController.dismiss(null, 'cancel');
    }
}
